export type AccountExitFailure = 'push-detach-failed' | 'sign-out-failed'

export type AccountExitResult
  = | { ok: true, authoritativeSessionEnded: boolean }
    | { ok: false, failure: AccountExitFailure }

export interface AccountExitDependencies {
  detachPush: (
    expectedUserId: string,
    expectedSessionId: string | null,
  ) => Promise<boolean>
  signOut: () => Promise<{ error?: unknown }>
  clearAccountState: () => Promise<void> | void
  notifyAccountEnded?: () => void
}

export interface AccountEntryDependencies {
  detachPush: (
    expectedUserId: string,
    expectedSessionId: string | null,
  ) => Promise<boolean>
  sanitizePush: () => Promise<boolean>
  clearAccountState: () => Promise<void> | void
  notifyAccountEnded?: () => void
}

export async function prepareAccountEntry(
  existingRegisteredUserId: string | null,
  dependencies: AccountEntryDependencies,
  existingRegisteredSessionId: string | null = null,
): Promise<boolean> {
  let oldSessionEnded = true
  if (existingRegisteredUserId) {
    try {
      oldSessionEnded = await dependencies.detachPush(
        existingRegisteredUserId,
        existingRegisteredSessionId,
      )
    }
    catch {
      oldSessionEnded = false
    }
    if (oldSessionEnded)
      dependencies.notifyAccountEnded?.()
  }
  const pushSanitized = await dependencies.sanitizePush()
  await dependencies.clearAccountState()
  return oldSessionEnded && pushSanitized
}

export async function completeAccountExit(
  expectedUserId: string | null,
  dependencies: AccountExitDependencies,
  expectedSessionId: string | null = null,
): Promise<AccountExitResult> {
  if (expectedUserId) {
    try {
      if (!(await dependencies.detachPush(expectedUserId, expectedSessionId))) {
        return { ok: false, failure: 'push-detach-failed' }
      }
    }
    catch {
      return { ok: false, failure: 'push-detach-failed' }
    }
  }

  if (expectedUserId) {
    dependencies.notifyAccountEnded?.()
    await dependencies.clearAccountState()
    return { ok: true, authoritativeSessionEnded: true }
  }

  try {
    const result = await dependencies.signOut()
    if (result.error)
      return { ok: false, failure: 'sign-out-failed' }
  }
  catch {
    return { ok: false, failure: 'sign-out-failed' }
  }

  dependencies.notifyAccountEnded?.()
  await dependencies.clearAccountState()
  return { ok: true, authoritativeSessionEnded: false }
}
